import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { inlineMediaUrl } from '../../../music24/promote/inlineMediaUrl';
import { useDirectedVideoContext } from '../../DirectedVideoContext';
import { resultMessages as messages } from '../../messages/resultMessages';
import { directedResultFile, directedResultPoster } from './resultRules';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the result; each part uses its own slice of it */
import styles from './Result.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The player of the finished video (design directed-video k1): the rendered file in a 16:9 frame on
 * black, the whole frame on view (`object-fit: contain`), with the browser controls. The poster is
 * the poster frame of the render, else the still of the first made clip; never the cover art. With
 * no file (the render is not ready, or the file was removed) it shows the poster and a line.
 */
const ResultPlayerComponent: React.FC = () => {
  const intl = useIntl();
  const { video, song } = useDirectedVideoContext();
  const file = directedResultFile(video);
  const poster = directedResultPoster(video, inlineMediaUrl);
  const label = intl.formatMessage(messages.player, { title: song.title });
  if (file === null) {
    return (
      <div className={styles.player} data-testid="directed-result-player">
        {poster !== null && <img src={poster} alt={label} />}
        <p className={styles.playerNote}>{intl.formatMessage(messages.notReady)}</p>
      </div>
    );
  }
  return (
    <div className={styles.player} data-testid="directed-result-player">
      {/* eslint-disable-next-line jsx-a11y/media-has-caption -- a made video has its lyrics in the picture, no caption track */}
      <video
        src={inlineMediaUrl(file)}
        poster={poster ?? undefined}
        aria-label={label}
        controls
        playsInline
        preload="metadata"
        data-testid="directed-result-video"
      />
    </div>
  );
};

export const ResultPlayer = TypedMemo(ResultPlayerComponent);
